import { useEffect, useState, useMemo } from 'react';
import './App.css';
import useAdaptivePerformance from './useAdaptivePerformance';
import {
  BouquetWebAccent,
  CatalogGlyph,
  HeroCornerWeb,
  SeasonalCorners,
  SeasonalDrift,
  isHalloweenThemeActive,
} from './halloween';
import { db } from './firebase';
import { collection, onSnapshot, query, doc } from 'firebase/firestore';

const logo = process.env.PUBLIC_URL + '/1by1 (2) (5).png';

const hardcodedBouquets = [];

const features = [
  { icon: '🌸', title: 'Fresh Blooms Daily', desc: 'Every bouquet is assembled with freshly sourced flowers to ensure lasting beauty and fragrance.' },
  { icon: '🎀', title: 'Handcrafted with Love', desc: 'Each arrangement is carefully hand-tied and wrapped by our passionate florists.' },
  { icon: '✨', title: 'Premium Quality', desc: 'We source only the finest stems — from Stargazer Lilies to imported Gypsophila and more.' },
  { icon: '🚚', title: 'Reliable Delivery', desc: 'We offer reliable delivery services to ensure your flowers arrive fresh and in perfect condition.' },
];

const petalData = [
  { left: '5%', delay: '0s', duration: '12s', char: '🌸' },
  { left: '15%', delay: '2s', duration: '15s', char: '🌺' },
  { left: '30%', delay: '5s', duration: '10s', char: '🌷' },
  { left: '50%', delay: '1s', duration: '14s', char: '🌸' },
  { left: '65%', delay: '7s', duration: '11s', char: '🌼' },
  { left: '80%', delay: '3s', duration: '16s', char: '🌹' },
  { left: '90%', delay: '9s', duration: '13s', char: '🌸' },
];

function useScrollReveal(dependencies = []) {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [dependencies]);
}

// 🌟 EDIT THIS ARRAY TO MANUALLY CHANGE THE BEST SELLERS 🌟
// Just type the name of the bouquets you want to feature here.
const MANUAL_BEST_SELLERS = [
  "Soft Thumbelina Bouquet 2",
  "Nami Bouquet 4"
];

function CatalogMaintenanceView({ status, isHalloweenSeason }) {
  return (
    <div className={`maintenance-page-wrapper${isHalloweenSeason ? ' halloween-season' : ''}`}>
      {/* Floating seasonal background */}
      {!isHalloweenSeason && <div className="petal-decoration" aria-hidden="true">
        {petalData.map((p, i) => (
          <span
            key={i}
            className="floating-petal"
            style={{ left: p.left, animationDelay: p.delay, animationDuration: p.duration }}
          >
            {p.char}
          </span>
        ))}
      </div>}
      {isHalloweenSeason && <HeroCornerWeb />}
      {isHalloweenSeason && <SeasonalDrift />}

      <div className="maintenance-ambient-glow glow-1" />
      <div className="maintenance-ambient-glow glow-2" />

      <main className="maintenance-card-container">
        {/* Solyminx Glowing Brand Header */}
        <div className="maintenance-logo-wrap">
          <div className="maintenance-logo-halo" />
          <img src={logo} alt="Solyminx Floral Studio" className="maintenance-logo-img" />
        </div>

        {/* Status Badge */}
        <div className="maintenance-badge">
          <span className="maintenance-badge-icon">{isHalloweenSeason ? <CatalogGlyph name="pumpkin" /> : '🌸'}</span>
          <span>Catalog Under Maintenance</span>
          <span className="maintenance-badge-sparkle">{isHalloweenSeason ? <CatalogGlyph name="star" /> : '✨'}</span>
        </div>

        {/* Headlines */}
        <h1 className="maintenance-title">
          {status.title || "We're Refreshing Our Blooms!"}
        </h1>

        <p className="maintenance-desc">
          {status.message || "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!"}
        </p>

        {/* Elegant Divider */}
        <div className="maintenance-divider">
          <div className="maintenance-line" />
          <span className="maintenance-flower-symbol">{isHalloweenSeason ? <CatalogGlyph name="moon" /> : '🌷'}</span>
          <div className="maintenance-line" />
        </div>

        {/* Direct Inquiries & Socials */}
        <div className="maintenance-actions-section">
          <p className="maintenance-actions-note">
            Need an urgent bouquet or custom floral order? You can still message us directly!
          </p>

          <div className="maintenance-btn-grid">
            <a
              href="https://www.facebook.com/profile.php?id=61563663364615"
              target="_blank"
              rel="noreferrer"
              className="maintenance-btn maintenance-btn-fb"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
              </svg>
              <span>Chat / Order on Facebook</span>
            </a>

            <a
              href="https://www.tiktok.com/@solyminx"
              target="_blank"
              rel="noreferrer"
              className="maintenance-btn maintenance-btn-tiktok"
            >
              <svg viewBox="0 0 448 512" width="20" height="20" fill="currentColor">
                <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25V349.38A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0l88 0a121.18 121.18 0 0 0 1.86 22.17h0A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z" />
              </svg>
              <span>Follow us on TikTok</span>
            </a>
          </div>
        </div>

        {/* Store Highlights Card */}
        <div className="maintenance-info-bar">
          <div className="maintenance-info-item">
            <span className="info-icon">{isHalloweenSeason ? <CatalogGlyph name="pin" /> : '📍'}</span>
            <span className="info-text">Silang, Cavite</span>
          </div>
          <div className="maintenance-info-dot">•</div>
          <div className="maintenance-info-item">
            <span className="info-icon">{isHalloweenSeason ? <CatalogGlyph name="clock" /> : '🕐'}</span>
            <span className="info-text">Mon – Sun, 9 AM – 7 PM</span>
          </div>
          <div className="maintenance-info-dot">•</div>
          <div className="maintenance-info-item">
            <span className="info-icon">{isHalloweenSeason ? <CatalogGlyph name="bow" /> : '🎀'}</span>
            <span className="info-text">Custom Orders Available</span>
          </div>
        </div>

        {/* Live Auto-Update Status Indicator */}
        <div className="maintenance-live-indicator">
          <span className="live-ping-dot" />
          <span className="live-status-msg">Live Sync Active · Catalog will automatically restore when live</span>
        </div>
      </main>

      <footer className="maintenance-footer">
        <p>© 2026 Solyminx Flower Shop · Handcrafted Elegance</p>
      </footer>
    </div>
  );
}

function App() {
  const isHalloweenSeason = isHalloweenThemeActive();
  const isLowPerformance = useAdaptivePerformance();
  const [bouquets, setBouquets] = useState(hardcodedBouquets);
  const [sortOrder, setSortOrder] = useState('default');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBouquet, setSelectedBouquet] = useState(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLowSlideVisible, setIsLowSlideVisible] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Real-time catalog down / maintenance status
  const [catalogStatus, setCatalogStatus] = useState({
    isDown: false,
    title: "We're Refreshing Our Blooms! 🌸",
    message: "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!"
  });

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'bouquets', '_catalog_status'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setCatalogStatus({
          isDown: !!data.isDown,
          title: data.title || "We're Refreshing Our Blooms! 🌸",
          message: data.message || "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!"
        });
      } else {
        setCatalogStatus({
          isDown: false,
          title: "We're Refreshing Our Blooms! 🌸",
          message: "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!"
        });
      }
    }, (err) => {
      console.warn("Could not fetch catalog status:", err);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'bouquets'));
    const unsub = onSnapshot(q, (snapshot) => {
      // Also extract status directly from bouquets query snapshot
      const statusDoc = snapshot.docs.find(d => d.id === '_catalog_status' || d.id === 'catalog_status');
      if (statusDoc) {
        const sData = statusDoc.data();
        setCatalogStatus({
          isDown: !!sData.isDown,
          title: sData.title || "We're Refreshing Our Blooms! 🌸",
          message: sData.message || "We are currently updating our flower catalog with fresh new arrangements and stock. We'll be back shortly!"
        });
      }

      const dbBouquets = snapshot.docs
        // November 1 arrangements stay archived in Solyminx Orders, including
        // their image URLs, but are no longer part of the public brochure.
        .filter(d => !d.id.startsWith('_') && d.id !== 'catalog_status' && d.data().name && !d.data().isNovemberFeatured)
        .map(doc => {
         const data = doc.data();
         return {
           id: doc.id,
           name: data.name,
           number: data.number || '',
           price: data.price,
           salePrice: data.salePrice || '',
           isBestSeller: data.isBestSeller || false,
           isAvailable: data.isAvailable !== false && !data.isSoldOut,
           image: data.image,
           mainFlowers: data.mainFlowers || [],
           fillers: data.fillers || [],
           position: data.position ?? 9999,
           createdAt: data.createdAt || '',
         };
      });
      // Sort by position first, then by createdAt as fallback
      dbBouquets.sort((a, b) => {
        if (a.position !== b.position) return a.position - b.position;
        return a.createdAt > b.createdAt ? -1 : 1;
      });

      // Compute dynamic variant numbers for items without an explicit number
      const nameCounts = {};
      dbBouquets.forEach(b => {
        if (!nameCounts[b.name]) nameCounts[b.name] = 0;
        nameCounts[b.name]++;
        b.computedNumber = nameCounts[b.name];
      });

      setBouquets([...dbBouquets, ...hardcodedBouquets]);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.custom-select-container')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const slideshowImages = useMemo(() => [
    process.env.PUBLIC_URL + '/images/Nami Bouquet (2).jfif',      // Nami 2 (UNANG LALABAS)
    process.env.PUBLIC_URL + '/images/Munchkin Bouquet (5).jfif', // Munchkin 8
    process.env.PUBLIC_URL + '/images/Munchkin Bouquet (6).jfif', // Munchkin 9
    process.env.PUBLIC_URL + '/images/Soft Thumbelina Bouquet (4).jfif', // Soft 4
    process.env.PUBLIC_URL + '/images/Munchkin bouquet (4).jfif', // Munchkin 7
    process.env.PUBLIC_URL + '/images/Soft Thumbelina bouquet (3).jfif', // Soft 3
    process.env.PUBLIC_URL + '/images/Veto (4).jfif',             // Veto 4
    process.env.PUBLIC_URL + '/images/Spring Thumbelina bouquet (1).jfif' // Spring 1
  ], []);

  useEffect(() => {
    if (!videoLoaded && !isLowPerformance) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % slideshowImages.length);
      }, 2500);
      return () => clearInterval(interval);
    }
  }, [videoLoaded, slideshowImages.length, isLowPerformance]);

  useEffect(() => {
    if (!isLowPerformance) {
      setIsLowSlideVisible(true);
      return undefined;
    }

    let cancelled = false;
    let fadeTimer;
    let swapStarted = false;
    const lowSlideCount = Math.min(4, slideshowImages.length);
    const nextIndex = (currentSlide + 1) % lowSlideCount;
    const changeTimer = setTimeout(() => {
      const nextImage = new Image();
      const swapImage = () => {
        if (cancelled || swapStarted) return;
        swapStarted = true;
        setIsLowSlideVisible(false);
        fadeTimer = setTimeout(() => {
          if (cancelled) return;
          setCurrentSlide(nextIndex);
          setIsLowSlideVisible(true);
        }, 360);
      };

      nextImage.onload = swapImage;
      nextImage.onerror = swapImage;
      nextImage.src = slideshowImages[nextIndex];
      if (nextImage.complete) swapImage();
    }, 6500);

    return () => {
      cancelled = true;
      clearTimeout(changeTimer);
      clearTimeout(fadeTimer);
    };
  }, [currentSlide, isLowPerformance, slideshowImages]);

  useScrollReveal([sortOrder, searchQuery]);

  const filteredBouquets = bouquets.filter(b => {
    const q = searchQuery.toLowerCase();
    return b.name.toLowerCase().includes(q) ||
      b.price.toLowerCase().includes(q) ||
      b.mainFlowers.some(f => f.toLowerCase().includes(q)) ||
      b.fillers.some(f => f.toLowerCase().includes(q));
  });

  const sortedBouquets = [...filteredBouquets].sort((a, b) => {
    if (sortOrder === 'default') return 0;
    const priceA = Number(a.price.split('-')[0].replace(/[^0-9.-]+/g, ""));
    const priceB = Number(b.price.split('-')[0].replace(/[^0-9.-]+/g, ""));
    return sortOrder === 'lowToHigh' ? priceA - priceB : priceB - priceA;
  });

  // Manually compute best sellers based on the MANUAL_BEST_SELLERS array above
  const bestSellers = useMemo(() => {
    const results = [];
    
    MANUAL_BEST_SELLERS.forEach(manualName => {
      // Find the first bouquet that matches this name (ignoring case)
      const found = bouquets.find(b => {
        const combinedName = (b.name + ' ' + (b.number || b.computedNumber || '')).toLowerCase().trim();
        const baseName = (b.name || '').toLowerCase().trim();
        const searchTarget = manualName.toLowerCase().trim();
        return combinedName.includes(searchTarget) || baseName === searchTarget;
      });
      if (found && !results.find(r => r.id === found.id)) {
        results.push(found);
      }
    });

    // Fallback if the user typed a name wrong, just show the first 2 bouquets
    if (results.length === 0 && bouquets.length >= 2) {
      return bouquets.slice(0, 2);
    }
    
    return results.slice(0, 2);
  }, [bouquets]);

  // If Catalog is marked as DOWN by Solyminx Admin, show the Maintenance screen
  if (catalogStatus.isDown) {
    return <CatalogMaintenanceView status={catalogStatus} isHalloweenSeason={isHalloweenSeason} />;
  }

  return (
    <div className={`brochure${isHalloweenSeason ? ' halloween-season' : ''}${isLowPerformance ? ' low-performance-mode' : ''}`}>
      {/* Floating seasonal decoration */}
      {!isHalloweenSeason && <div className="petal-decoration" aria-hidden="true">
        {petalData.map((p, i) => (
          <span
            key={i}
            className="floating-petal"
            style={{ left: p.left, animationDelay: p.delay, animationDuration: p.duration }}
          >
            {p.char}
          </span>
        ))}
      </div>}

      {/* ===== HERO ===== */}
      <section className="hero" id="hero">
        {/* Low mode keeps one lightweight image mounted and swaps it slowly. */}
        {isLowPerformance ? (
          <div
            className={`hero-static-bg ${isLowSlideVisible ? 'is-visible' : 'is-fading'}`}
            style={{ backgroundImage: `url("${slideshowImages[currentSlide % Math.min(4, slideshowImages.length)]}")` }}
            aria-hidden="true"
          />
        ) : !videoLoaded && (
          <div className="hero-slideshow">
            {slideshowImages.map((img, idx) => (
              <div
                key={idx}
                className={`hero-slide ${idx === currentSlide ? 'active' : ''}`}
                style={{ backgroundImage: `url("${img}")` }}
              />
            ))}
          </div>
        )}

        {!isLowPerformance && <video
          className={`hero-video-bg ${videoLoaded ? 'loaded' : ''}`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlay={() => setVideoLoaded(true)}
          style={{ transition: 'opacity 1s ease' }}
          onTimeUpdate={(e) => {
            const video = e.target;
            if (video.duration) {
              if (video.currentTime >= video.duration - 0.8) {
                video.style.opacity = '0.3';
              } else if (video.currentTime < 0.8) {
                video.style.opacity = '1';
              }
            }
          }}
        >
          <source src={process.env.PUBLIC_URL + '/images/vid2.MOV'} type="video/quicktime" />
          <source src={process.env.PUBLIC_URL + '/images/vid2.MOV'} type="video/mp4" />
          Your browser does not support the video tag.
        </video>}
        <div className="hero-overlay" aria-hidden="true" />

        {isHalloweenSeason && <HeroCornerWeb />}
        {isHalloweenSeason && <SeasonalDrift />}

        <div className="hero-content">
          <img src={logo} alt="Flower Shop Logo" className="hero-logo" />
          <span className="hero-tagline">~ Blooming with love ~</span>
          <h1 className="hero-title">
            <span>Solyminx</span><br />Flower Shop
          </h1>
          <p className="hero-subtitle">
            Elegantly handcrafted bouquets made from the finest blooms,<br />
            wrapped in warmth and delivered with joy.
          </p>

          <div className="hero-divider" aria-hidden="true">
            <div className="hero-divider-line" />
            <span className="hero-divider-icon">{isHalloweenSeason ? <CatalogGlyph name="pumpkin" /> : '🌸'}</span>
            <div className="hero-divider-line" />
          </div>

          {/* Scroll Hint */}
          <div className="hero-scroll-hint" aria-hidden="true">
            <span className="scroll-label">Scroll</span>
            <div className="scroll-arrow-wrap">
              <div className="scroll-arrow" />
              <div className="scroll-arrow scroll-arrow-2" />
            </div>
          </div>

        </div>
      </section>

      {/* ===== INTRO STRIP ===== */}
      <div className="intro-strip">
        <p>
          "Nature spends months perfecting a single bloom — we take those fleeting moments of beauty and wrap them into memories that last a lifetime."
        </p>
      </div>

      {/* ===== BEST SELLERS ===== */}
      {bestSellers.length > 0 && (
        <section className="best-sellers-section reveal" id="best-sellers">
          {isHalloweenSeason && <SeasonalCorners />}
          <div className="bs-header">
            <span className="bs-label">Highly Recommended</span>
            <h2 className="bs-title">Our <em>Best Sellers</em></h2>
            <p className="bs-desc">
              The most loved arrangements by our beautiful customers. Handpicked for perfection.
            </p>
          </div>
          <div className="bs-grid">
            {bestSellers.map((bouquet, index) => (
              <div 
                key={bouquet.id} 
                className={`bs-card reveal reveal-delay-${index + 1}`}
                onClick={() => setSelectedBouquet(bouquet)}
              >
                <div className="bs-image-wrap">
                  {isHalloweenSeason && index === 1 && <BouquetWebAccent side="right" depth="deep" />}
                  {bouquet.isBestSeller && (
                    <div className="bs-badge">🔥 Best Seller</div>
                  )}
                  {!bouquet.isAvailable && (
                    <div className="bs-card-blocking-overlay">
                      <div className="bs-card-blocking-banner">🚫 NOT AVAILABLE</div>
                    </div>
                  )}
                  <img
                    src={bouquet.image}
                    alt={bouquet.name}
                    style={{ filter: !bouquet.isAvailable ? 'grayscale(45%) brightness(0.72)' : 'none' }}
                  />
                </div>
                <div className="bs-info">
                  <div className="bs-info-header">
                    <h3 className="bs-name">{bouquet.name} {bouquet.number || bouquet.computedNumber}</h3>
                    <div className="bs-price-box">
                      {bouquet.salePrice ? (
                        <>
                          <span className="bs-price-original">{bouquet.price}</span>
                          <span className="bs-price-sale">{bouquet.salePrice}</span>
                        </>
                      ) : (
                        <span className="bs-price-regular">{bouquet.price}</span>
                      )}
                    </div>
                  </div>
                  <div className="bs-divider" />
                  <p className="bs-flowers">
                    <strong>Main Flowers:</strong> {bouquet.mainFlowers.join(', ')}
                  </p>
                  <button className="bs-action-btn">
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ===== PRODUCTS ===== */}
      <section className="products-section" id="collection">
        <div className="section-header reveal">
          <span className="section-label">Our Collection</span>
          <h2 className="section-title">Beautiful <em>Bouquets</em></h2>
          <p className="section-desc">
            Lovingly crafted arrangements, each with a unique personality and price to match every occasion.
          </p>
          <div className="section-underline" aria-hidden="true">
            <span>{isHalloweenSeason ? <CatalogGlyph name="moon" /> : '🌷'}</span>
          </div>
        </div>

        <div className="controls-container reveal">
          <div className="search-wrapper">
            <span className="search-icon" aria-hidden="true">{isHalloweenSeason ? <CatalogGlyph name="search" /> : '🔍'}</span>
            <input
              type="text"
              placeholder="Search bouquets or flowers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="sort-wrapper">
            <label>Sort by Price:</label>
            <div className="custom-select-container">
              <button
                className={`custom-select-trigger ${isDropdownOpen ? 'active' : ''}`}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                aria-haspopup="listbox"
                aria-expanded={isDropdownOpen}
              >
                <span>
                  {sortOrder === 'default' && 'Featured'}
                  {sortOrder === 'lowToHigh' && 'Low to High'}
                  {sortOrder === 'highToLow' && 'High to Low'}
                </span>
                <span className="select-arrow-icon">▼</span>
              </button>
              {isDropdownOpen && (
                <ul className="custom-select-options" role="listbox">
                  <li
                    className={`custom-select-option ${sortOrder === 'default' ? 'selected' : ''}`}
                    role="option"
                    aria-selected={sortOrder === 'default'}
                    onClick={() => {
                      setSortOrder('default');
                      setIsDropdownOpen(false);
                    }}
                  >
                    Featured
                  </li>
                  <li
                    className={`custom-select-option ${sortOrder === 'lowToHigh' ? 'selected' : ''}`}
                    role="option"
                    aria-selected={sortOrder === 'lowToHigh'}
                    onClick={() => {
                      setSortOrder('lowToHigh');
                      setIsDropdownOpen(false);
                    }}
                  >
                    Low to High
                  </li>
                  <li
                    className={`custom-select-option ${sortOrder === 'highToLow' ? 'selected' : ''}`}
                    role="option"
                    aria-selected={sortOrder === 'highToLow'}
                    onClick={() => {
                      setSortOrder('highToLow');
                      setIsDropdownOpen(false);
                    }}
                  >
                    High to Low
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="products-grid">
          {sortedBouquets.map((bouquet, index) => (
            <div
              key={bouquet.id}
              className={`product-card ${!bouquet.isAvailable ? 'is-unavailable' : ''}`}
            >
              <div
                className="product-card-image-wrap"
                onClick={() => setSelectedBouquet(bouquet)}
                style={{ cursor: 'pointer' }}
              >
                {isHalloweenSeason && index % 6 === 1 && (
                  <BouquetWebAccent
                    side={Math.floor(index / 6) % 2 === 0 ? 'left' : 'right'}
                    depth={Math.floor(index / 6) % 3 === 1 ? 'deep' : 'standard'}
                  />
                )}
                {bouquet.isBestSeller && (
                  <div className="product-badge-bestseller">🔥 Best Seller</div>
                )}
                {!bouquet.isAvailable && (
                  <div className="product-card-blocking-overlay">
                    <div className="product-card-blocking-banner">🚫 NOT AVAILABLE</div>
                  </div>
                )}
                <img
                  src={bouquet.image}
                  alt={`${bouquet.name} ${bouquet.number} — ${bouquet.price}`}
                  loading={index < 6 ? 'eager' : 'lazy'}
                  style={{ filter: !bouquet.isAvailable ? 'grayscale(45%) brightness(0.72)' : 'none' }}
                />
                <div className="product-badge">
                  {bouquet.salePrice ? (
                    <>
                      <span className="product-badge-original-price">{bouquet.price}</span>
                      <span className="product-badge-sale-price">{bouquet.salePrice}</span>
                    </>
                  ) : bouquet.price}
                </div>
              </div>

              <div className="product-card-body">
                <h3 className="product-name">{bouquet.name} {bouquet.number || bouquet.computedNumber}</h3>

                <div className="product-divider" />

                <div className="flower-section">
                  <div className="flower-label">Main Flowers</div>
                  <div className="flower-tags">
                    {bouquet.mainFlowers.map((f, i) => (
                      <span key={i} className="flower-tag">{f}</span>
                    ))}
                  </div>
                </div>

                <div className="flower-section">
                  <div className="flower-label" style={{ color: 'var(--rose)', '--dot-color': 'var(--rose)' }}>Fillers</div>
                  <div className="flower-tags">
                    {bouquet.fillers.map((f, i) => (
                      <span key={i} className="flower-tag filler-tag">{f}</span>
                    ))}
                  </div>
                </div>

                <div className="price-pillar">
                  {bouquet.salePrice ? (
                    <>
                      <span className="price-pillar-original">{bouquet.price}</span>
                      <span className="price-pillar-sale">{bouquet.salePrice}</span>
                    </>
                  ) : bouquet.price}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>



      {/* ===== FEATURES ===== */}
      <section className="features-section" id="why-us">
        {isHalloweenSeason && <SeasonalCorners />}
        <div className="features-inner">
          <div className="section-header reveal">
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-title">Flowers crafted with <em>passion</em></h2>
            <div className="section-underline" aria-hidden="true">
              <span>{isHalloweenSeason ? <CatalogGlyph name="star" /> : '🌿'}</span>
            </div>
          </div>
          <div className="features-grid">
            {features.map((f, i) => (
              <div key={i} className={`feature-card reveal reveal-delay-${i + 1}`}>
                <span className="feature-icon" aria-hidden="true">{isHalloweenSeason ? <CatalogGlyph name={['flower', 'bow', 'star', 'truck'][i]} /> : f.icon}</span>
                <h3 className="feature-title">{f.title}</h3>
                <p className="feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ===== CONTACT / FOOTER ===== */}
      <footer className="contact-section" id="contact">
        {isHalloweenSeason && <SeasonalCorners />}
        <div className="footer-layout">
          {/* Column 1: Brand & Socials */}
          <div className="footer-col brand-col">
            <img src={process.env.PUBLIC_URL + '/1by1 (2).png'} alt="Flower Shop Logo" className="footer-logo" />
            <h3 className="footer-brand-title">Solyminx Flower Shop</h3>
            <p className="footer-brand-desc">
              Elegantly handcrafted bouquets made from the finest blooms, wrapped in warmth and delivered with joy.
            </p>
            <div className="footer-socials">
              <a href="https://www.facebook.com/profile.php?id=61563663364615" target="_blank" rel="noreferrer" title="Facebook">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                </svg>
              </a>
              <a href="https://www.tiktok.com/@solyminx" target="_blank" rel="noreferrer" title="TikTok">
                <svg viewBox="0 0 448 512" width="20" height="20" fill="currentColor">
                  <path d="M448 209.91a210.06 210.06 0 0 1-122.77-39.25V349.38A162.55 162.55 0 1 1 185 188.31v89.89a74.62 74.62 0 1 0 52.23 71.18V0l88 0a121.18 121.18 0 0 0 1.86 22.17h0A122.18 122.18 0 0 0 381 102.39a121.43 121.43 0 0 0 67 20.14Z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div className="footer-col links-col">
            <h4 className="footer-heading">Explore</h4>
            <ul>
              <li><a href="#hero">Home</a></li>
              <li><a href="#collection">Our Collection</a></li>
              <li><a href="#why-us">Why Choose Us</a></li>
            </ul>
          </div>

          {/* Column 3: Contact Info */}
          <div className="footer-col contact-col">
            <h4 className="footer-heading">Get in Touch</h4>
            <ul className="footer-contact-list">
              <li>
                <span className="icon" aria-hidden="true">{isHalloweenSeason ? <CatalogGlyph name="pin" /> : '📍'}</span> Silang, Cavite
              </li>
              <li>
                <span className="icon" aria-hidden="true">{isHalloweenSeason ? <CatalogGlyph name="clock" /> : '🕐'}</span> Mon – Sun, 9:00 AM – 7:00 PM
              </li>
              <li>
                <span className="icon" aria-hidden="true">{isHalloweenSeason ? <CatalogGlyph name="mail" /> : '💌'}</span> Available on request
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <span className="footer-flower" aria-hidden="true">{isHalloweenSeason ? <CatalogGlyph name="moon" /> : '🌸'}</span>
          <p>© 2026 Solyminx Flower Shop · Crafted with elegance</p>
          <span className="footer-flower" aria-hidden="true">{isHalloweenSeason ? <CatalogGlyph name="star" /> : '🌸'}</span>
        </div>
      </footer>

      {/* ===== IMAGE MODAL ===== */}
      {/* ===== PRODUCT MODAL ===== */}
      {selectedBouquet && (
        <div className="image-modal-overlay" onClick={() => setSelectedBouquet(null)}>
          <div className="image-modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setSelectedBouquet(null)}>✕</button>
            <div className="image-modal-content">
              <div className="image-modal-image-wrap" style={{ position: 'relative' }}>
                {isHalloweenSeason && <BouquetWebAccent side="right" />}
                {selectedBouquet.isBestSeller && (
                  <div className="modal-bestseller-badge">🔥 Best Seller</div>
                )}
                {!selectedBouquet.isAvailable && (
                  <div className="modal-unavailable-overlay-block">
                    <div className="modal-unavailable-stamp">
                      <span className="stamp-icon">🚫</span>
                      <div className="stamp-text">
                        <div className="stamp-main">NOT AVAILABLE</div>
                        <div className="stamp-sub">CURRENTLY OUT OF STOCK</div>
                      </div>
                    </div>
                  </div>
                )}
                <img
                  src={selectedBouquet.image}
                  alt={selectedBouquet.name}
                  style={{ filter: !selectedBouquet.isAvailable ? 'grayscale(45%) brightness(0.7)' : 'none' }}
                />
              </div>
              <div className="image-modal-details">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <h2 className="modal-product-name" style={{ margin: 0 }}>
                    {selectedBouquet.name} {selectedBouquet.number || selectedBouquet.computedNumber}
                  </h2>
                  {!selectedBouquet.isAvailable && (
                    <span className="modal-unavailable-tag">
                      🚫 Sold Out / Not Available
                    </span>
                  )}
                </div>
                {selectedBouquet.salePrice ? (
                  <div className="modal-price-pill sale">
                    <span className="modal-price-original">{selectedBouquet.price}</span>
                    <span className="modal-price-sale">{selectedBouquet.salePrice}</span>
                  </div>
                ) : (
                  <div className="modal-price-pill">{selectedBouquet.price}</div>
                )}

                <div className="modal-divider" />

                <div className="modal-section">
                  <div className="modal-section-label">Main Flowers</div>
                  <div className="modal-tags">
                    {selectedBouquet.mainFlowers.map((f, i) => (
                      <span key={i} className="modal-tag">{f}</span>
                    ))}
                  </div>
                </div>

                <div className="modal-section">
                  <div className="modal-section-label filler-label">Fillers</div>
                  <div className="modal-tags">
                    {selectedBouquet.fillers.map((f, i) => (
                      <span key={i} className="modal-tag filler-tag">{f}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
